import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { FileText, FolderInput, FolderPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getChildFolders, getFilesInFolder } from './folderTypes';
import FolderNode, { FolderTreeShared } from './FolderNode';
import FolderDropZone from './FolderDropZone';
import DocumentTreeItem from './DocumentTreeItem';

interface FolderTreeProps {
  shared: FolderTreeShared;
  /** Move the given file ids into targetFolderId (null => category root). Persists via backend. */
  onMoveFiles: (ids: string[], targetFolderId: string | null) => void;
  /** True while folders are being fetched from the backend. */
  loading?: boolean;
  /** Opens the New Folder dialog (used by the empty state). */
  onNewFolder?: () => void;
}

export default function FolderTree({ shared, onMoveFiles, loading, onNewFolder }: FolderTreeProps) {
  const [dragIds, setDragIds] = useState<string[]>([]);

  const sensors = useSensors(
    // Small activation distance so a plain click still selects / previews.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const rootFolders = getChildFolders(shared.folders, shared.category, null);
  const rootFiles = getFilesInFolder(shared.files, null);

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    // Multi-drag only applies when the grabbed file is part of the current selection.
    const ids = shared.selectedIds.has(id) && shared.selectedIds.size > 1
      ? [...shared.selectedIds]
      : [id];
    setDragIds(ids);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event;
    const ids = dragIds;
    setDragIds([]);
    if (!over || ids.length === 0) return;
    const targetFolderId = (over.data.current?.folderId ?? null) as string | null;
    onMoveFiles(ids, targetFolderId);
  };

  const overlayName = useMemo(() => {
    if (dragIds.length !== 1) return '';
    return shared.files.find(f => f.id === dragIds[0])?.name ?? '';
  }, [dragIds, shared.files]);

  const isEmpty = rootFolders.length === 0 && rootFiles.length === 0;

  // Folders still loading and nothing to show yet — a light spinner keeps the tab responsive.
  if (loading && isEmpty) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען תיקיות…</span>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragIds([])}
    >
      <div className="space-y-1">
        {/* Root drop target — drop here to move files out of any folder. */}
        <FolderDropZone id="root" folderId={null}>
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
            <FolderInput className="w-4 h-4" />
            כל הקבצים
          </div>
        </FolderDropZone>

        {rootFolders.map((folder) => (
          <FolderNode key={folder.id} folder={folder} depth={0} shared={shared} />
        ))}

        {rootFiles.map((file) => (
          <DocumentTreeItem
            key={file.id}
            file={file}
            depth={0}
            isSelected={shared.selectedIds.has(file.id)}
            isAndroid={shared.isAndroid}
            selectionMode={shared.selectionMode}
            tileHandlers={shared.tileHandlers}
            onFileClick={shared.onFileClick}
            onToggleSelection={shared.onToggleSelection}
            onDownload={shared.onDownload}
          />
        ))}

        {isEmpty && (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-4">אין עדיין תיקיות או קבצים</p>
            {onNewFolder && (
              <Button variant="outline" onClick={onNewFolder} className="gap-1.5">
                <FolderPlus className="w-4 h-4" />
                צור תיקייה ראשונה
              </Button>
            )}
          </div>
        )}
      </div>

      <DragOverlay dropAnimation={null}>
        {dragIds.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium shadow-lg">
            <FileText className="w-4 h-4" />
            {dragIds.length > 1 ? `${dragIds.length} קבצים` : overlayName}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
